import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { isCode, type Part } from './copy';

/** Marketing column width from DESIGN.md. */
export const PAGE_WIDTH = 1120;

const MONO = 'IBM Plex Mono';

/** mono-sm: eyebrows, captions, statuses, grades. Never uppercase. */
export const monoSm: SystemStyleObject<Theme> = {
  fontFamily: MONO,
  fontSize: '0.75rem',
  lineHeight: 1.4,
  letterSpacing: '0.02em',
  color: 'var(--ink-muted)',
};

/** Colour is a second channel only — the grade word itself is always present. */
const GRADE_COLOR: Record<string, string> = {
  high: 'var(--success)',
  medium: 'var(--warning)',
  advertised: 'var(--ink-muted)',
};

export function Eyebrow({ children }: { children: ReactNode }) {
  return <Typography component="p" sx={{ ...monoSm, mb: 1.5 }}>{children}</Typography>;
}

export function InlineCode({ children }: { children: ReactNode }) {
  return (
    <Box
      component="code"
      sx={{
        fontFamily: MONO,
        fontSize: '0.875em',
        color: 'var(--ink)',
        backgroundColor: 'var(--bg-elevated)',
        border: '1px solid var(--rule)',
        borderRadius: '2px',
        px: '4px',
        py: '1px',
      }}
    >
      {children}
    </Box>
  );
}

/** Renders a prose run in which API values are marked up as `code`. */
export function Prose({ parts }: { parts: Part[] }) {
  return (
    <>
      {parts.map((part, i) =>
        isCode(part) ? <InlineCode key={i}>{part.code}</InlineCode> : <span key={i}>{part}</span>,
      )}
    </>
  );
}

/** The written confidence grade, tinted per role. The word never disappears. */
export function ConfidenceWord({
  grade,
  sx,
}: {
  grade: string;
  sx?: SystemStyleObject<Theme>;
}) {
  return (
    <Box
      component="span"
      sx={{ ...monoSm, color: GRADE_COLOR[grade] ?? 'var(--ink-muted)', ...sx }}
    >
      {grade}
    </Box>
  );
}

type SectionProps = {
  id?: string;
  /** id of the heading that names this section. */
  labelledBy: string;
  eyebrow?: string;
  /** When omitted the caller renders its own heading carrying `labelledBy`. */
  title?: string;
  /** The hairline divider above the section. The hero opens without one. */
  divider?: boolean;
  titleMaxWidth?: number | string;
  children: ReactNode;
  sx?: SystemStyleObject<Theme>;
};

/**
 * One page section: page gutters, the 1120 column, the hairline divider that
 * separates sections, and the optional eyebrow + title pair.
 */
export function Section({
  id,
  labelledBy,
  eyebrow,
  title,
  divider = true,
  titleMaxWidth = '22ch',
  children,
  sx,
}: SectionProps) {
  return (
    <Box component="section" id={id} aria-labelledby={labelledBy} sx={{ px: { xs: 3, md: 6 } }}>
      <Box
        sx={{
          maxWidth: PAGE_WIDTH,
          mx: 'auto',
          borderTop: divider ? '1px solid var(--rule)' : 'none',
          py: { xs: 4, md: 6 },
          ...sx,
        }}
      >
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        {title && (
          <Typography variant="h2" id={labelledBy} sx={{ maxWidth: titleMaxWidth, mb: { xs: 3, md: 4 } }}>
            {title}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}

/**
 * Text link in `accent-deep`, underlined on hover. Router paths route in-app;
 * `mailto:` and in-page hashes fall back to a plain anchor.
 */
export function TextLink({
  to,
  children,
  sx,
}: {
  to: string;
  children: ReactNode;
  sx?: SystemStyleObject<Theme>;
}) {
  const internal = to.startsWith('/');
  const style: SystemStyleObject<Theme> = {
    color: 'var(--accent-deep)',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' },
    ...sx,
  };
  return internal ? (
    <Box component={RouterLink} to={to} sx={style}>
      {children}
    </Box>
  ) : (
    <Box component="a" href={to} sx={style}>
      {children}
    </Box>
  );
}
